<template>
  <li class="q-list-group-item q-list-group-item q-d-flex q-justify-content-between q-align-items-start q-px-0 q-p-1 q-text-start">
    <button
        type="button"
        class="aggregation-suggestion__link q-text-decoration-none q-link-secondary"
        :class="{'q-btn q-btn-danger q-rounded-3 q-shadow-sm q-me-2': isMobileView}"
        @click="applyFilter"
    >
      <span
          class="aggregation-suggestion__label"
          v-html="highlightedLabel"
      ></span>
    </button>
  </li>
</template>

<script setup lang="ts">
import { computed, inject, defineProps } from 'vue'
import { useUrlStore } from '@/store/SerpPage/UrlStore'
import { useSearchQueryStore } from '@/store/SearchQueryStore'
import { storeToRefs } from 'pinia'
import { useMobileViewStore } from '@/store/MobileViewStore'
import { highlightText } from '@/core/highlight'
import type { AggregationSuggestionItem as AggregationSuggestionItemType } from '@/store/AutocompletePopup/AutocompleteAggregationsStore'

const props = defineProps<{
  field: string
  item: AggregationSuggestionItemType
}>()

const SmartSearchConfig = inject('SmartSearchConfig')?.value as { appConfiguration: { redirectionUrl: string } } | undefined

const urlStore = useUrlStore()
const searchQueryStore = useSearchQueryStore()
const { searchQuery } = storeToRefs(searchQueryStore)

const mobileViewStore = useMobileViewStore()
const { isMobileView } = storeToRefs(mobileViewStore)

const redirectionUrl = computed(() => SmartSearchConfig?.appConfiguration?.redirectionUrl ?? '/search')

const highlightedLabel = computed(() => highlightText(
  props.item.displayLabel,
  searchQuery.value ?? '',
  'aggregation-suggestion__highlight'
))

function applyFilter() {
  const params = new URLSearchParams()
  const query = searchQueryStore.searchQuery
  if (query) {
    params.set('q', query)
  }

  params.set(`f[${props.field}]`, JSON.stringify([props.item.value]))

  const targetPath = buildTargetPath(params)
  urlStore.goToSerpPage(targetPath, query ?? '')
}

function buildTargetPath(params: URLSearchParams): string {
  const base = redirectionUrl.value || '/'
  const queryString = params.toString()
  if (!queryString) {
    return base
  }

  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}${queryString}`
}
</script>

<style scoped>
.aggregation-suggestion__link {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-align: left;
}

.aggregation-suggestion__link:hover,
.aggregation-suggestion__link:focus {
  text-decoration: underline;
}

.aggregation-suggestion__link:focus {
  outline: none;
}

.aggregation-suggestion__label {
  font-size: 0.75em;
}

@media (max-width: 767px) {
  .aggregation-suggestion__link {
    color: #fff;
    background-color: var(--q-red, #ef2d56);
    padding: 0.25em 0.5em;
    border-radius: 0.5em;
    box-shadow: var(--q-box-shadow-sm, 0 .125rem .25rem rgba(0, 0, 0, .075));
  }
}

:deep(mark.aggregation-suggestion__highlight) {
  background: #dc35453b !important;
  padding: 0 !important;
}
</style>
